import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Form,
  Input,
  Select,
  Checkbox,
  DatePicker,
  TimePicker,
  Card,
  Space,
  Spin,
  Row,
  Col,
  Table,
} from "antd";
import { PageHeader } from "@ant-design/pro-components";
import { Save, Plus, Trash2 } from "lucide-react";

import CustomButton from "@/components/common/Button";

import SLAConfigurationService from "@/services/APIs/SLAConfigurationService";
import DropdownService from "@/services/APIs/DropdownService";

import HolidayListModal from "./HolidayListModal";
import dayjs from "dayjs";

const { Option } = Select;

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

// TypeScript Interfaces
interface UserOption {
  label: string;
  value: string;
}

interface ModuleOption {
  label: string;
  value: string;
}

interface WorkingDayForm {
  day: string;
  start_time?: { format: (format: string) => string };
  end_time?: { format: (format: string) => string };
}

interface PriorityForm {
  priority: string;
  response_time?: { format: (format: string) => string };
  resolution_time?: { format: (format: string) => string };
  is_default?: boolean;
}

const AddSLAConfigurationPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);

  // Watch for conditional visibility
  const isDefaultSla = Form.useWatch("default_service_level_agreement", form);

  // Fetch SLA configuration data for edit mode
  const { data: slaConfig, isLoading: isLoadingConfig } = useQuery({
    queryKey: ["sla-configuration", id],
    queryFn: () => SLAConfigurationService.getConfigurationById(id!),
    enabled: isEditMode,
  });

  // Fetch business calendars/holiday lists
  const {
    data: calendars,
    isLoading: isLoadingCalendars,
    refetch: refetchCalendars,
  } = useQuery({
    queryKey: ["business-calendars"],
    queryFn: () => SLAConfigurationService.getBusinessCalendars(),
  });

  const handleHolidayListCreated = (newId?: string) => {
    setIsHolidayModalOpen(false);
    if (newId) {
      refetchCalendars();
      form.setFieldsValue({ holiday_list: newId });
    }
  };

  // Mock options
  // Fetch Users
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["users-dropdown"],
    queryFn: () => DropdownService.getUsers(),
  });

  // Map users to options
  const userOptions: UserOption[] =
    usersData?.map((u: UserOption) => ({
      label: u.label ?? "Unknown User",
      value: u.value,
    })) ?? [];

  // Fetch Modules
  const { data: modulesData, isLoading: isLoadingModules } = useQuery({
    queryKey: ["sla-modules"],
    queryFn: () => SLAConfigurationService.getModules(),
  });

  // Helper to format "PTS_app" to "Pts App"
  const formatModuleLabel = (str: string) => {
    if (!str) {
      return "Unknown Module";
    }
    return str
      .split(/_| /) // split by underscore or space
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Map modules
  const moduleOptions: ModuleOption[] =
    modulesData
      ?.map((m: any) => {
        // Determine the display label source
        let rawLabel = "";

        // Handle array item case (e.g. ["PTS_app"])
        const item = Array.isArray(m) ? m[0] : m;

        if (typeof item === "string") {
          rawLabel = item;
        } else if (item?.module) {
          rawLabel = item.module;
        } else if (item?.name) {
          rawLabel = item.name;
        }

        // Apply formatting if we found a label, otherwise fallback to JSON
        const label = rawLabel
          ? formatModuleLabel(rawLabel)
          : JSON.stringify(m);

        // Determine value
        const value =
          typeof item === "string"
            ? item
            : (item?.module ?? item?.id ?? item?.name ?? JSON.stringify(item));

        return { label, value };
      })
      .filter(
        (opt: ModuleOption) =>
          opt.value &&
          opt.value !== "fitbit_connection" &&
          !opt.label.includes("{")
      ) ?? [];

  // Helper to map workday int (0=Monday) to string for UI
  const getDayName = (dayIndex: number) => DAYS_OF_WEEK[dayIndex];
  // Helper to map UI string ("Monday") to int for API
  const getDayIndex = (dayName: string) => DAYS_OF_WEEK.indexOf(dayName);

  // Populate form when editing
  useEffect(() => {
    if (slaConfig) {
      // Data Transformation: API -> Form
      const formattedConfig = {
        ...slaConfig,
        // Dates
        start_date: slaConfig.start_date
          ? dayjs(slaConfig.start_date)
          : undefined,
        end_date: slaConfig.end_date ? dayjs(slaConfig.end_date) : undefined,

        // Ensure holiday_list is just an ID if it came as an object
        holiday_list:
          typeof slaConfig.holiday_list === "object"
            ? (slaConfig.holiday_list as any)?.id
            : slaConfig.holiday_list,

        // Working Days (Int -> String + dayjs Time)
        working_days: slaConfig.working_days?.map(wd => ({
          ...wd,
          day: getDayName(wd.workday), // 0 -> "Monday"
          start_time: wd.start_time
            ? dayjs(wd.start_time, "HH:mm:ss")
            : undefined,
          end_time: wd.end_time ? dayjs(wd.end_time, "HH:mm:ss") : undefined,
        })),

        // Priorities (Responses Time mapping)
        priorities: slaConfig.priorities?.map(p => ({
          ...p,
          response_time: p.response_time
            ? dayjs(p.response_time, "HH:mm:ss")
            : undefined,
          resolution_time: p.resolution_time
            ? dayjs(p.resolution_time, "HH:mm:ss")
            : undefined,
        })),

        // Multi-select Transforms: Access inner value from API object list
        modules: slaConfig.modules?.map(m => m.id ?? m.module),
        sla_users: slaConfig.sla_users?.map(u => u.id ?? u.user),

        // Handle singular string status fields - map to form field names
        sla_fulfilled_on_statuses:
          slaConfig.sla_fulfilled_on_status || undefined,
        sla_pause_on_statuses: slaConfig.sla_pause_on_status || undefined,
      };

      form.setFieldsValue(formattedConfig);
    }
  }, [slaConfig, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      // Data Transformation: Form -> API Payload
      // Extract plural status fields to exclude them from the spread ...rest
      const { sla_fulfilled_on_statuses, sla_pause_on_statuses, ...rest } =
        values;

      const payload = {
        ...rest,
        enabled: !!values.enabled,
        default_service_level_agreement:
          !!values.default_service_level_agreement,
        start_date: values.start_date?.format("YYYY-MM-DD"),
        end_date: values.end_date?.format("YYYY-MM-DD"),

        // Working Days (String "Monday" -> Int 0)
        working_days:
          values.working_days?.map((wd: WorkingDayForm) => ({
            workday: getDayIndex(wd.day),
            start_time: wd.start_time?.format("HH:mm:ss"),
            end_time: wd.end_time?.format("HH:mm:ss"),
          })) ?? [],

        // Priorities
        priorities:
          values.priorities?.map((p: PriorityForm) => ({
            ...p,
            response_time: p.response_time?.format("HH:mm:ss"),
            resolution_time: p.resolution_time?.format("HH:mm:ss"),
            is_default: !!p.is_default,
          })) ?? [],

        // Transform Arrays of Strings back to Arrays of Objects
        modules: values.modules?.map((m: string) => ({ module: m })) ?? [],
        sla_users: values.sla_users?.map((u: string) => ({ user: u })) ?? [],

        // Map to singular keys (ensure single string or null). Convert empty array to null.
        sla_fulfilled_on_status:
          Array.isArray(sla_fulfilled_on_statuses) &&
          sla_fulfilled_on_statuses.length === 0
            ? null
            : (sla_fulfilled_on_statuses ?? null),
        sla_pause_on_status:
          Array.isArray(sla_pause_on_statuses) &&
          sla_pause_on_statuses.length === 0
            ? null
            : (sla_pause_on_statuses ?? null),

        escalations: values.escalations ?? [],
        // Explicitly ensure holiday_list is just the ID string
        holiday_list:
          typeof values.holiday_list === "object" && values.holiday_list
            ? (values.holiday_list as { id: string }).id
            : values.holiday_list,
      };

      if (isEditMode) {
        await SLAConfigurationService.updateConfiguration(id!, payload);
      } else {
        await SLAConfigurationService.createConfiguration(payload);
      }

      navigate("/sla-configurations");
    } catch (error: any) {
      console.error("Submit Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    window.history.back();
  };

  if (isEditMode && isLoadingConfig) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spin size="large" tip="Loading SLA configuration..." />
      </div>
    );
  }

  return (
    <>
      <Card
        title={
          <PageHeader
            className="custom-page-header"
            title={
              isEditMode ? "Edit SLA Configuration" : "Add SLA Configuration"
            }
            onBack={handleCancel}
            extra={
              <Space>
                <CustomButton
                  type="primary"
                  icon={<Save size={16} />}
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  title={
                    isEditMode ? "Update Changes" : "Save SLA Configuration"
                  }
                >
                  {isEditMode ? "Update" : "Save"}
                </CustomButton>
              </Space>
            }
          />
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            enabled: true,
            default_service_level_agreement: false,
            priorities: [],
            working_days: [],
            escalations: [],
            modules: [],
            sla_users: [],
            sla_fulfilled_on_statuses: undefined,
            sla_pause_on_statuses: undefined,
          }}
        >
          <div className="mb-4 text-base font-semibold text-gray-800">
            Basic Details
          </div>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Service level Name"
                name="name"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="e.g. Standard SLA" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Apply On"
                name="entity_type"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select placeholder="Select Type">
                  <Option value="Incident">Issue</Option>
                  <Option value="Ticket">Ticket</Option>
                  <Option value="Case">Case</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label=" "
                name="enabled"
                valuePropName="checked"
                colon={false}
              >
                <Checkbox>Active</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <div className="mb-4 mt-6 text-base font-semibold text-gray-800">
            Assignment Conditions
          </div>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="default_service_level_agreement"
                valuePropName="checked"
              >
                <Checkbox>
                  Default Service Level Agreement (Apply on all tickets)
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          {!isDefaultSla && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Modules" name="modules">
                  <Select
                    mode="multiple"
                    placeholder="Select Module(s)"
                    allowClear
                    loading={isLoadingModules}
                  >
                    {moduleOptions.map((m: any) => (
                      <Option key={m.value} value={m.value}>
                        {m.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Users" name="sla_users">
                  <Select
                    mode="multiple"
                    placeholder="Select User(s)"
                    loading={isLoadingUsers}
                    allowClear
                  >
                    {userOptions.map((e: any) => (
                      <Option key={e.value} value={e.value}>
                        {e.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          <div className="mb-4 mt-6 text-base font-semibold text-gray-800">
            Validity Period
          </div>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Start Date"
                name="start_date"
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="End Date" name="end_date">
                <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <div className="mb-4 mt-6 text-base font-semibold text-gray-800">
            Response & Resolution
          </div>
          <Form.Item
            name="apply_sla_for_resolution"
            valuePropName="checked"
            className="mb-6"
          >
            <Checkbox>Apply SLA targets for Resolution Time</Checkbox>
          </Form.Item>

          <div className="mb-4 mt-6 text-base font-semibold text-gray-800">
            Priorities
          </div>
          <Form.List name="priorities">
            {(fields, { add, remove }) => (
              <div className="mb-6">
                <Table
                  dataSource={fields}
                  pagination={false}
                  rowKey="key"
                  bordered
                  size="small"
                  columns={[
                    {
                      title: "Priority*",
                      key: "priority",
                      render: (_, field) => (
                        <Form.Item
                          {...field}
                          name={[field.name, "priority"]}
                          rules={[{ required: true, message: "Required" }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            placeholder="Select Priority Level"
                            style={{ width: "100%" }}
                          >
                            {PRIORITIES.map(p => (
                              <Option key={p} value={p}>
                                {p}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ),
                    },
                    {
                      title: "Default",
                      key: "is_default",
                      width: 80,
                      align: "center",
                      render: (_, field) => (
                        <Form.Item
                          {...field}
                          name={[field.name, "is_default"]}
                          valuePropName="checked"
                          style={{ marginBottom: 0 }}
                        >
                          <Checkbox />
                        </Form.Item>
                      ),
                    },
                    {
                      title: "Response Time*",
                      key: "response_time",
                      render: (_, field) => (
                        <Form.Item
                          {...field}
                          name={[field.name, "response_time"]}
                          rules={[{ required: true, message: "Required" }]}
                          style={{ marginBottom: 0 }}
                        >
                          <TimePicker
                            format="HH:mm:ss"
                            style={{ width: "100%" }}
                            placeholder="Hrs:Mins:Sec"
                          />
                        </Form.Item>
                      ),
                    },
                    {
                      title: "Resolution Time*",
                      key: "resolution_time",
                      render: (_, field) => (
                        <Form.Item
                          {...field}
                          name={[field.name, "resolution_time"]}
                          rules={[{ required: true, message: "Required" }]}
                          style={{ marginBottom: 0 }}
                        >
                          <TimePicker
                            format="HH:mm:ss"
                            style={{ width: "100%" }}
                            placeholder="Hrs:Mins:Sec"
                          />
                        </Form.Item>
                      ),
                    },
                    {
                      title: "Action",
                      key: "action",
                      width: 60,
                      align: "center",
                      render: (_, field) => (
                        <CustomButton
                          type="text"
                          danger
                          icon={<Trash2 size={16} />}
                          onClick={() => remove(field.name)}
                          title="Remove Priority"
                        />
                      ),
                    },
                  ]}
                  footer={() => (
                    <CustomButton
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<Plus size={16} />}
                    >
                      Add Priority Level
                    </CustomButton>
                  )}
                />
              </div>
            )}
          </Form.List>

          <div className="mb-4 mt-6 text-base font-semibold text-gray-800">
            Status Details
          </div>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="SLA Fulfilled On – Status*"
                name="sla_fulfilled_on_statuses"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select placeholder="Select Status">
                  <Option value="Open">Open</Option>
                  <Option value="Replied">Replied</Option>
                  <Option value="Resolved">Resolved</Option>
                  <Option value="Closed">Closed</Option>
                  <Option value="On Hold">On Hold</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="SLA Paused On – Status"
                name="sla_pause_on_statuses"
              >
                <Select placeholder="Select Status">
                  <Option value="Open">Open</Option>
                  <Option value="Replied">Replied</Option>
                  <Option value="Resolved">Resolved</Option>
                  <Option value="Closed">Closed</Option>
                  <Option value="On Hold">On Hold</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <div className="mb-4 mt-6 text-base font-semibold text-gray-800">
            Holidays
          </div>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Holiday List"
                style={{ marginBottom: 0 }} // manage spacing manually due to flex
              >
                <div style={{ display: "flex", gap: "8px" }}>
                  <Form.Item
                    name="holiday_list"
                    rules={[{ required: true, message: "Required" }]}
                    noStyle
                  >
                    <Select
                      placeholder="Select Holiday List"
                      loading={isLoadingCalendars}
                      allowClear
                      style={{ flex: 1 }}
                    >
                      {calendars?.map((cal: any) => (
                        <Option key={cal.value} value={cal.value}>
                          {cal.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <CustomButton
                    icon={<Plus size={16} />}
                    onClick={() => setIsHolidayModalOpen(true)}
                    title="Add New Holiday List"
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
          <div className="mb-4 mt-6 text-base font-semibold text-gray-800">
            Working Days
          </div>
          <Form.List name="working_days">
            {(fields, { add, remove }) => (
              <Table
                dataSource={fields}
                pagination={false}
                rowKey="key"
                bordered
                size="small"
                columns={[
                  {
                    title: "Workday*",
                    key: "day",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "day"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select placeholder="Select Day">
                          {DAYS_OF_WEEK.map(d => (
                            <Option key={d} value={d}>
                              {d}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Start Time*",
                    key: "start_time",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "start_time"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <TimePicker
                          format="HH:mm:ss"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "End Time*",
                    key: "end_time",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "end_time"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <TimePicker
                          format="HH:mm:ss"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Action",
                    key: "action",
                    width: 60,
                    align: "center",
                    render: (_, field) => (
                      <CustomButton
                        type="text"
                        danger
                        icon={<Trash2 size={16} />}
                        onClick={() => remove(field.name)}
                        title="Remove Day"
                      />
                    ),
                  },
                ]}
                footer={() => (
                  <CustomButton
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<Plus size={16} />}
                  >
                    Add Working Day
                  </CustomButton>
                )}
              />
            )}
          </Form.List>

          <div className="mb-4 mt-6 text-base font-semibold text-gray-800">
            Escalations
          </div>
          <p className="mb-4 text-gray-500">
            Multi-stage actions based on elapsed SLA % or absolute time.
          </p>

          <Form.List name="escalations">
            {(fields, { add, remove }) => (
              <Table
                dataSource={fields}
                pagination={false}
                rowKey="key"
                bordered
                size="small"
                columns={[
                  {
                    title: "Trigger*",
                    key: "trigger",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "trigger"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          placeholder="Select Trigger"
                          style={{ width: "100%" }}
                        >
                          <Option value="At Risk (Warn)">At Risk (Warn)</Option>
                          <Option value="At Risk (Warn %)">
                            At Risk (Warn %)
                          </Option>
                          <Option value="Breached">Breached</Option>
                          <Option value="Elapsed %">Elapsed %</Option>
                          <Option value="Elapsed Minutes">
                            Elapsed Minutes
                          </Option>
                        </Select>
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Action*",
                    key: "action",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "action"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          placeholder="Select Action"
                          style={{ width: "100%" }}
                        >
                          <Option value="Notify">Notify</Option>
                          <Option value="Reassign">Reassign</Option>
                          <Option value="Create PTS Task">
                            Create PTS Task
                          </Option>
                          <Option value="Page On-Call">Page On-Call</Option>
                          <Option value="Open Incident">Open Incident</Option>
                        </Select>
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Target*",
                    key: "target",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "target"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Target (e.g. Tier 2 Queue)" />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Notes",
                    key: "notes",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "notes"]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Optional note" />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Action",
                    key: "action_btn",
                    width: 60,
                    align: "center",
                    render: (_, field) => (
                      <CustomButton
                        type="text"
                        danger
                        icon={<Trash2 size={16} />}
                        onClick={() => remove(field.name)}
                        title="Remove"
                      />
                    ),
                  },
                ]}
                footer={() => (
                  <CustomButton
                    type="dashed"
                    onClick={() => add()}
                    icon={<Plus size={16} />}
                    block
                  >
                    Add Step
                  </CustomButton>
                )}
              />
            )}
          </Form.List>
        </Form>
      </Card>

      <HolidayListModal
        open={isHolidayModalOpen}
        onClose={handleHolidayListCreated}
      />
    </>
  );
};

export default AddSLAConfigurationPage;
